import { useState } from 'react';
import type { SubscriptionRecipients } from '@/types';

/**
 * Who the email goes to. Cc and Bcc are separate fields on the report server rather than more
 * entries in the To list — showing them together lost the distinction, and saving them together
 * promoted every copied-in address to a direct recipient.
 */
export function RecipientsEditor({
  value,
  onChange
}: {
  value: SubscriptionRecipients;
  onChange: (v: SubscriptionRecipients) => void;
}) {
  const [draft, setDraft] = useState('');

  function add() {
    const v = draft.trim();
    if (!v) return;
    if (v.includes('@')) {
      onChange({ ...value, emails: [...value.emails, v] });
    } else {
      onChange({ ...value, groups: [...value.groups, { objectId: v, displayName: v }] });
    }
    setDraft('');
  }

  function removeEmail(idx: number) {
    onChange({ ...value, emails: value.emails.filter((_, i) => i !== idx) });
  }

  function removeGroup(idx: number) {
    onChange({ ...value, groups: value.groups.filter((_, i) => i !== idx) });
  }

  return (
    <div>
      <div className="recipient-list">
        {value.groups.map((g, i) => (
          <span key={`g-${i}`} className="recipient-chip" style={{ background: 'var(--kt-green-bg)', borderColor: 'var(--kt-green-mid)' }}>
            {g.displayName}
            <span className="x" onClick={() => removeGroup(i)}>&times;</span>
          </span>
        ))}
        {value.emails.map((e, i) => (
          <span key={`e-${i}`} className="recipient-chip">
            {e}
            <span className="x" onClick={() => removeEmail(i)}>&times;</span>
          </span>
        ))}
        {value.emails.length === 0 && value.groups.length === 0 && (
          <span className="recipient-empty">No recipients yet.</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="input"
          style={{ flex: 1 }}
          type="text"
          placeholder="Add email or Entra group..."
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button className="btn-secondary" onClick={add}>Add</button>
      </div>

      <div className="field-row" style={{ marginTop: 16 }}>
        <AddressList
          label="Cc"
          value={value.cc ?? []}
          onChange={cc => onChange({ ...value, cc })}
        />
        <AddressList
          label="Bcc"
          value={value.bcc ?? []}
          onChange={bcc => onChange({ ...value, bcc })}
        />
      </div>
      <div className="field">
        <label>Reply-to <span style={{ fontWeight: 400, color: 'var(--kt-grey-400)' }}>(optional)</span></label>
        <input
          className="input"
          type="email"
          placeholder="Replies go to the sender unless set"
          value={value.replyTo ?? ''}
          onChange={e => onChange({ ...value, replyTo: e.target.value.trim() || null })}
        />
      </div>
    </div>
  );
}

/** A semicolon/comma-separated address list — the same shape the report server stores. */
function AddressList({
  label,
  value,
  onChange
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <input
        className="input"
        type="text"
        placeholder="Separate addresses with ;"
        value={value.join('; ')}
        onChange={e =>
          onChange(e.target.value.split(/[;,]/).map(s => s.trim()).filter(Boolean))
        }
      />
    </div>
  );
}
